import { Component, effect, inject, OnDestroy, ViewChild } from '@angular/core';
import { TaskLog, TaskLogService, TaskLogSeverity, TaskLogsSortByEnum, TaskLogTaskId } from 'lion-api';
import { FilterMetadata, SelectItem, SortMeta } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { DownloadUtilService, SimpleMessageService, TrackingIdService, UserStore } from '@shared/data';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-task-log',
  templateUrl: './task-log.component.html',
  styleUrl: './task-log.component.scss',
  standalone: false
})
export class TaskLogComponent implements OnDestroy {
  @ViewChild(Table)
  table: Table | undefined;

  taskLogs: TaskLog[] = [];
  selectedTaskLogs: TaskLog[] = [];
  hasTaskLogs = false;

  loading = true;
  // Pagination
  first = 0;
  page = 0;
  rows = 10;
  totalRecords?: number = 0;

  selectedSeverity: TaskLogSeverity | undefined;
  selectedTaskId: TaskLogTaskId | undefined;
  dialogVisible = false;
  readonly severityOptions: SelectItem[] = [
    {
      label: 'Alle',
      value: null
    },
    {
      label: TaskLogSeverity.Info,
      value: TaskLogSeverity.Info
    },
    {
      label: TaskLogSeverity.Warn,
      value: TaskLogSeverity.Warn
    },
    {
      label: TaskLogSeverity.Error,
      value: TaskLogSeverity.Error
    }
  ];
  readonly taskIdOptions: SelectItem[] = [
    {
      label: 'Alle',
      value: null
    },
    {
      label: TaskLogTaskId.Archivierung,
      value: TaskLogTaskId.Archivierung
    },
    {
      label: TaskLogTaskId.Cleanup,
      value: TaskLogTaskId.Cleanup
    },
    {
      label: TaskLogTaskId.CleanCache,
      value: TaskLogTaskId.CleanCache
    },
    {
      label: TaskLogTaskId.Feiertagskalender,
      value: TaskLogTaskId.Feiertagskalender
    },
    {
      label: TaskLogTaskId.Fusionslauf,
      value: TaskLogTaskId.Fusionslauf
    },
    {
      label: TaskLogTaskId.Kundenexport,
      value: TaskLogTaskId.Kundenexport
    },
    {
      label: TaskLogTaskId.Kundenupdate,
      value: TaskLogTaskId.Kundenupdate
    },
    {
      label: TaskLogTaskId.Limitupdate,
      value: TaskLogTaskId.Limitupdate
    },
    {
      label: TaskLogTaskId.Mailversand,
      value: TaskLogTaskId.Mailversand
    },
    {
      label: TaskLogTaskId.Manuelleeinstaende,
      value: TaskLogTaskId.Manuelleeinstaende
    },
    {
      label: TaskLogTaskId.MdsConnectionReset,
      value: TaskLogTaskId.MdsConnectionReset
    },
    {
      label: TaskLogTaskId.MdsHeartbeat,
      value: TaskLogTaskId.MdsHeartbeat
    },
    {
      label: TaskLogTaskId.Nachverzinsung,
      value: TaskLogTaskId.Nachverzinsung
    },
    {
      label: TaskLogTaskId.Produktverwaltung,
      value: TaskLogTaskId.Produktverwaltung
    },
    {
      label: TaskLogTaskId.Report,
      value: TaskLogTaskId.Report
    },
    {
      label: TaskLogTaskId.Send,
      value: TaskLogTaskId.Send
    },
    {
      label: TaskLogTaskId.Slareport,
      value: TaskLogTaskId.Slareport
    },
    {
      label: TaskLogTaskId.Tradeconsumer,
      value: TaskLogTaskId.Tradeconsumer
    },
    {
      label: TaskLogTaskId.UpdateFixedDateLaufzeiten,
      value: TaskLogTaskId.UpdateFixedDateLaufzeiten
    },
    {
      label: TaskLogTaskId.Vortagesauswertung,
      value: TaskLogTaskId.Vortagesauswertung
    }
  ];
  private sort: TaskLogsSortByEnum[] = [TaskLogsSortByEnum.AscLogDate];
  private userStore = inject(UserStore);
  hasTaskLogPermission = this.userStore.hasTaskLogPermission;

  private tasklogService = inject(TaskLogService);
  private translateService = inject(TranslateService);
  private trackingIdService = inject(TrackingIdService);
  private downloadUtilService = inject(DownloadUtilService);

  private simpleMessageService = inject(SimpleMessageService);
  private readonly MESSAGE_KEY = 'task-log';
  protected readonly messageState = this.simpleMessageService.getMessageState(this.MESSAGE_KEY);

  private pollingSubscription: Subscription | undefined;

  constructor() {
    effect(() => {
      if (this.hasTaskLogPermission()) {
        this.checkForTaskLogs();
        if (!this.pollingSubscription || this.pollingSubscription.closed) {
          this.pollingSubscription = interval(60000).subscribe(() => {
            this.checkForTaskLogs();
            if (this.dialogVisible) {
              this.table?.onLazyLoad.emit(this.table.createLazyLoadMetadata());
            }
          });
        }
      } else if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
      }
    });
  }

  loadLazy(event: TableLazyLoadEvent) {
    if (event && event.sortField && typeof event.sortField === 'string' && event.sortOrder) {
      this.setTaskLogSortOrder(event.sortField, event.sortOrder);
      const filters = event.filters;

      if (filters) {
        if (filters['severity']) {
          const severityFilters = filters['severity'] as FilterMetadata[];
          this.selectedSeverity = (severityFilters[0] as FilterMetadata).value;
        }

        if (filters['taskId']) {
          const taskIdFilters = filters['taskId'] as FilterMetadata[];
          this.selectedTaskId = (taskIdFilters[0] as FilterMetadata).value;
        }
      }

      this.load(event.first ?? 0, (event.first ?? 0) / (event.rows ?? 0), event.rows ?? 0, this.selectedSeverity, this.selectedTaskId, this.sort);
    }
  }

  ngOnDestroy(): void {
    this.pollingSubscription?.unsubscribe();
  }

  confirmSelectedTaskLogs() {
    this.tasklogService
      .tasklogsConfirmPut(
        this.selectedTaskLogs.map((value) => value.id!),
        this.trackingIdService.generateTrackingId()
      )
      .subscribe({
        next: () => {
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('shared.task-log.feedback.confirm.success.detail')
          );

          this.selectedTaskLogs = [];
          this.load(this.first, this.page, this.rows, this.selectedSeverity, this.selectedTaskId, this.sort);
        },
        error: (error) => {
          this.showConfirmError(error);
        }
      });
  }

  onShowDialog() {
    this.dialogVisible = true;
    this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
  }

  onExport() {
    this.tasklogService.tasklogsExportGet(this.trackingIdService.generateTrackingId()).subscribe({
      next: (next) => {
        this.downloadUtilService.downloadCsvFile(next, this.translateService.instant('shared.task-log.export.filename'));
      },
      error: () => {
        this.simpleMessageService.sendInlineErrorMessage(this.MESSAGE_KEY, this.translateService.instant('shared.error.frontend.details'));
      }
    });
  }

  private checkForTaskLogs() {
    // Nur prüfen, ob Logs vorhanden sind, um den Button anzuzeigen.
    this.tasklogService.tasklogsGet(undefined, undefined, 0, 1, undefined, this.trackingIdService.generateTrackingId()).subscribe({
      next: (response) => {
        this.hasTaskLogs = (response.total_elements ?? 0) > 0;
        this.handleInitialLoadDialog();
      },
      error: (e) => {
        console.error(`Fehler beim Prüfen der TaskLogs: ${e}`);
      }
    });
  }

  private load(first: number, page: number, rows: number, severity?: TaskLogSeverity, taskId?: TaskLogTaskId, sort?: TaskLogsSortByEnum[]) {
    this.first = first;
    this.page = page;
    this.rows = rows;
    this.loading = true;

    this.tasklogService.tasklogsGet(severity, taskId, page, rows, sort, this.trackingIdService.generateTrackingId()).subscribe({
      next: (response) => {
        this.taskLogs = response.taskLogs ?? [];
        this.totalRecords = response.total_elements;
        this.hasTaskLogs = (this.totalRecords ?? 0) > 0;

        this.handleInitialLoadDialog();

        this.loading = false;
      },
      error: (e) => {
        this.showConfirmError(e);
        console.error(`Fehler beim Laden der TaskLogs: ${e}`);
        this.loading = false;
      }
    });
  }

  private handleInitialLoadDialog() {
    if (this.hasTaskLogs && sessionStorage.getItem('initialLoad') !== 'false') {
      this.dialogVisible = true;
      sessionStorage.setItem('initialLoad', 'false');
    }
  }

  private getTaskLogSortOrder(sortEntry: SortMeta) {
    if (sortEntry.field == 'logDate') {
      return sortEntry.order === -1 ? TaskLogsSortByEnum.DescLogDate : TaskLogsSortByEnum.AscLogDate;
    }

    return;
  }

  private setTaskLogSortOrder(field: string, order: number): void {
    const sortEnumEntry = this.getTaskLogSortOrder({
      field: field,
      order: order
    });

    if (sortEnumEntry) {
      this.sort = [];
      this.sort.push(sortEnumEntry);
    }
  }

  private showConfirmError(error: unknown): void {
    this.simpleMessageService.sendInlineErrorMessage(
      this.MESSAGE_KEY,
      this.translateService.instant('shared.task-log.feedback.confirm.error.detail')
    );
    console.error(error);
  }
}
